import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, Eye, Monitor, Tablet, Smartphone } from 'lucide-react';
import { BlockRenderer, type CmsBlock } from '../components/blocks/BlockRenderer';
import { useBranding } from '../contexts/BrandingContext';

interface PreviewPageData {
  id: string;
  slug: string;
  title: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  status: 'draft' | 'published';
  blocks: CmsBlock[];
}

function getTenantSlug(): string {
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug) return envSlug;
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 2 && parts[0] !== 'localhost') return parts[0];
  return 'tenant1';
}

export function CmsPreviewPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const branding = useBranding();

  const [page, setPage] = useState<PreviewPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceWidth, setDeviceWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    fetch(`/public/preview/${encodeURIComponent(token)}`, {
      headers: { 'x-tenant-slug': getTenantSlug() },
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          setError('This preview link has expired or is invalid.');
          return;
        }
        if (res.status === 404) {
          setError('Preview page not found.');
          return;
        }
        if (!res.ok) throw new Error('Failed to load preview');
        const data = (await res.json()) as PreviewPageData;
        setPage(data);
        document.title = `[Preview] ${data.metaTitle ?? data.title} — ${branding.name}`;
      })
      .catch(() => setError('Failed to load preview. Please try again.'))
      .finally(() => setLoading(false));

    return () => {
      document.title = branding.name;
    };
  }, [token, branding.name]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-zinc-50 px-4">
        <AlertTriangle size={32} className="text-amber-400" />
        <p className="text-sm text-zinc-600 text-center max-w-sm">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="text-xs text-zinc-500 hover:text-zinc-800 underline"
        >
          Go to dashboard
        </button>
      </div>
    );
  }

  if (!page) return null;

  const DEVICES = [
    { label: 'Mobile', icon: Smartphone, width: 375 },
    { label: 'Tablet', icon: Tablet, width: 768 },
    { label: 'Desktop', icon: Monitor, width: null },
  ] as const;

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Preview banner */}
      <div className="sticky top-0 z-50 flex items-center gap-3 bg-amber-400 px-4 py-2 text-xs font-medium text-amber-900">
        <Eye size={12} className="flex-shrink-0" />
        <span className="flex-1 truncate">
          Preview — &quot;{page.title}&quot; — not publicly visible
        </span>
        {/* Device switcher */}
        <div className="flex items-center gap-1 bg-amber-300/60 rounded-md px-1 py-0.5">
          {DEVICES.map(({ label, icon: Icon, width }) => (
            <button
              key={label}
              onClick={() => setDeviceWidth(width)}
              title={width ? `${label} (${width}px)` : label}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] transition-colors ${
                deviceWidth === width
                  ? 'bg-amber-900 text-amber-50'
                  : 'hover:bg-amber-400/60'
              }`}
            >
              <Icon size={11} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Page content — constrained when device width is set */}
      <div
        className="mx-auto transition-all duration-300 bg-white min-h-screen"
        style={deviceWidth ? { maxWidth: deviceWidth } : undefined}
      >
        <div className="space-y-6 py-6 px-4">
          {page.blocks
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((block) => (
              <div key={block.id}>
                <BlockRenderer block={block} />
              </div>
            ))}
          {page.blocks.length === 0 && (
            <div className="text-center py-24 text-zinc-400 text-sm">
              This page has no content yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
